import { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { messageApi } from "../api/messageApi";

export const ChatContext = createContext();

export const ChatProvider = ({ children }) => {
  const { currentUser, isAuthenticated, socket, onlineUsers } = useAuth();
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [unseenMessages, setUnseenMessages] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [listingContext, setListingContext] = useState(null);
  
  // NEW: Draft conversation state
  const [isDraftConversation, setIsDraftConversation] = useState(false);

  // Debug logging
  const debugLog = (message, data = {}) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[ChatContext] ${message}`, data);
    }
  };

  // Check if user is online
  const isUserOnline = (userId) => {
    const isOnline = onlineUsers.includes(userId);
    debugLog(`User ${userId} online status:`, isOnline);
    return isOnline;
  };

  const getUsers = async () => {
    if (!isAuthenticated) {
      debugLog("Not authenticated, skipping getUsers");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      debugLog("Fetching users...");
      const data = await messageApi.getUsers();
      debugLog("Users API response:", data);
      
      if (data.success) {
        setUsers(data.users);
        // FIXED: Properly handle unseenMessages from backend
        setUnseenMessages(data.unseenMessages || {});
        debugLog("Users set successfully:", { 
          userCount: data.users.length, 
          unseenMessages: data.unseenMessages 
        });
      } else {
        throw new Error(data.message || "Failed to fetch users");
      }
    } catch (error) {
      console.error("[ChatContext] Get users error:", error);
      setError(error.message || "Failed to load users");
      setUsers([]);
      setUnseenMessages({});
    } finally {
      setLoading(false);
    }
  };

  // NEW: Check if conversation exists without creating it
  const checkExistingConversation = async (userId) => {
    if (!isAuthenticated || !userId || !currentUser?._id) {
      debugLog("Cannot check conversation:", { 
        isAuthenticated, 
        userId, 
        currentUserId: currentUser?._id 
      });
      return null;
    }

    try {
      debugLog("Checking existing conversation for user:", userId);
      const data = await messageApi.getExistingMessages(userId);
      debugLog("Existing messages check response:", data);
      
      if (data.success && data.hasConversation) {
        return {
          messages: data.messages || [],
          conversationId: data.conversationId
        };
      } else {
        return null; // No existing conversation
      }
    } catch (error) {
      console.error("[ChatContext] Check existing conversation error:", error);
      return null;
    }
  };

  const getMessages = async (userId) => {
    if (!isAuthenticated || !userId || !currentUser?._id) {
      debugLog("Cannot get messages:", { 
        isAuthenticated, 
        userId, 
        currentUserId: currentUser?._id 
      });
      return;
    }
    
    setLoading(true);
    setError(null);

    try {
      debugLog("Fetching messages for user:", userId);
      const data = await messageApi.getMessages(userId);
      debugLog("Messages API response:", data);
      
      if (data.success) {
        setMessages(data.messages);
        setIsDraftConversation(false); // Real conversation exists
        debugLog("Messages set successfully:", { 
          messageCount: data.messages.length 
        });
      } else {
        throw new Error(data.message || "Failed to fetch messages");
      }
    } catch (error) {
      console.error("[ChatContext] Get messages error:", error);
      setError(error.message || "Failed to load messages");
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (messageData) => {
    if (!isAuthenticated || !selectedUser || !messageData.content?.trim()) {
      debugLog("Cannot send message:", { 
        isAuthenticated, 
        hasSelectedUser: !!selectedUser, 
        hasContent: !!messageData.content?.trim() 
      });
      return { success: false, error: "Invalid message data" };
    }

    try {
      const messageWithSenderAndReceiver = {
        ...messageData,
        senderId: currentUser._id || currentUser.id,
        receiverId: selectedUser._id || selectedUser.id,
      };

      debugLog("Sending message:", messageWithSenderAndReceiver);
      const data = await messageApi.sendMessage(messageWithSenderAndReceiver);
      debugLog("Send message API response:", data);
      
      if (data.success) {
        // Handle message format properly
        const newMessage = {
          _id: data.newMessage._id,
          senderId: currentUser._id || currentUser.id,
          receiverId: selectedUser._id || selectedUser.id,
          content: messageData.content,
          text: messageData.content,
          createdAt: data.newMessage.createdAt || new Date().toISOString(),
          seen: false
        };
        
        console.log("✅ Adding sent message to UI:", newMessage);
        setMessages((prevMessages) => [...prevMessages, newMessage]);
        
        // NEW: If this was a draft conversation, it's now real
        if (isDraftConversation) {
          setIsDraftConversation(false);
          // Refresh users list to show the new conversation
          setTimeout(() => getUsers(), 500);
        }
        
        return { success: true };
      } else {
        debugLog("Send message failed:", data);
        return { success: false, error: data.message };
      }
    } catch (error) {
      console.error("[ChatContext] Send message error:", error);
      return { success: false, error: error.message };
    }
  };

  const markMessagesAsSeen = async (userId) => {
    // Don't try to mark messages as seen for draft conversations
    if (isDraftConversation) return;
    
    try {
      debugLog("Marking messages as seen for user:", userId);
      await messageApi.markMessagesSeen(userId);

      // Update unseenMessages state properly
      setUnseenMessages((prev) => ({
        ...prev,
        [userId]: 0,
      }));
      debugLog("Messages marked as seen successfully");
    } catch (error) {
      console.error("[ChatContext] Mark messages seen error:", error);
    }
  };

  // NEW: Set selected user with draft conversation support
  const setSelectedUserWithListing = async (user, listing = null) => {
    setSelectedUser(user);
    setListingContext(listing);
    setMessages([]); // Clear previous messages
    
    debugLog("Setting user with listing context:", { user, listing });
    
    // Check if conversation already exists
    const userId = user._id || user.id;
    const existingConversation = await checkExistingConversation(userId);
    
    if (existingConversation) {
      // Real conversation exists, load messages normally
      debugLog("Existing conversation found, loading messages");
      setMessages(existingConversation.messages);
      setIsDraftConversation(false);
      markMessagesAsSeen(userId);
    } else {
      // No conversation exists, set as draft
      debugLog("No existing conversation, setting as draft");
      setIsDraftConversation(true);
      setMessages([]); // Empty messages for draft
    }
  };

  // Modified setSelectedUser for normal conversation selection
  const setSelectedUserNormal = async (user) => {
    setSelectedUser(user);
    setListingContext(null);
    
    if (user) {
      const userId = user._id || user.id;
      // For normal selection (from sidebar), always load existing conversation
      await getMessages(userId);
      markMessagesAsSeen(userId);
    }
  };

  // NEW: Function to report a conversation
  const reportConversation = async (conversationId, reason) => {
    try {
      debugLog("Reporting conversation:", { conversationId, reason });
      const response = await messageApi.reportConversation(conversationId, reason);
      
      if (response.success) {
        debugLog("Conversation reported successfully");
        return { success: true };
      } else {
        return { success: false, error: response.message };
      }
    } catch (error) {
      console.error("[ChatContext] Report conversation error:", error);
      return { success: false, error: error.message };
    }
  };

  // Better socket message subscription
  const setupSocketListeners = () => {
    if (!socket || !currentUser) {
      debugLog("Cannot setup socket listeners:", { 
        hasSocket: !!socket, 
        socketConnected: socket?.connected,
        hasCurrentUser: !!currentUser 
      });
      return;
    }

    debugLog("Setting up socket listeners", {
      socketId: socket.id,
      connected: socket.connected
    });

    // Clean up existing listeners first
    socket.off("newMessage");

    // Set up new message listener
    socket.on("newMessage", (newMessage) => {
      console.log("📨 SOCKET MESSAGE RECEIVED:", {
        messageId: newMessage._id,
        senderId: newMessage.senderId,
        receiverId: newMessage.receiverId,
        content: newMessage.content || newMessage.text,
        hasContent: !!(newMessage.content || newMessage.text),
        rawMessage: newMessage
      });
      
      const currentUserId = currentUser._id || currentUser.id;
      const selectedUserId = selectedUser?._id || selectedUser?.id;
      
      // Handle both string IDs and object IDs
      const messageSenderId = typeof newMessage.senderId === 'object' 
        ? newMessage.senderId._id 
        : newMessage.senderId;
      const messageReceiverId = typeof newMessage.receiverId === 'object' 
        ? newMessage.receiverId._id 
        : newMessage.receiverId;
      
      console.log("💬 Message routing check:", {
        currentUserId,
        selectedUserId,
        messageSenderId,
        messageReceiverId,
        isForCurrentUser: messageReceiverId === currentUserId,
        isChatOpen: !!selectedUserId,
        isChatWithSender: messageSenderId === selectedUserId,
        isDraft: isDraftConversation
      });

      const isCurrentUserReceiver = messageReceiverId === currentUserId;
      
      if (!isCurrentUserReceiver) {
        console.log("❌ Message not for current user, ignoring");
        return;
      }

      const isChatOpenWithSender = selectedUserId && messageSenderId === selectedUserId;

      // Normalize message format
      const normalizedMessage = {
        _id: newMessage._id,
        senderId: messageSenderId,
        receiverId: messageReceiverId,
        content: newMessage.content || newMessage.text,
        text: newMessage.content || newMessage.text,
        createdAt: newMessage.createdAt,
        seen: newMessage.seen || false
      };

      if (isChatOpenWithSender) {
        console.log("✅ Adding message to current chat");
        normalizedMessage.seen = true;
        setMessages((prevMessages) => [...prevMessages, normalizedMessage]);
        
        // NEW: If this was a draft conversation, it's now real
        if (isDraftConversation) {
          setIsDraftConversation(false);
        }
        
        markMessagesAsSeen(messageSenderId);
      } else {
        console.log("📩 Updating unseen count for sender:", messageSenderId);
        setUnseenMessages((prevUnseenMessages) => ({
          ...prevUnseenMessages,
          [messageSenderId]: (prevUnseenMessages[messageSenderId] || 0) + 1,
        }));
        
        // Also refresh the users list to update the conversation
        getUsers();
      }
    });
  };

  // Set up socket listeners when socket connects or changes
  useEffect(() => {
    if (socket && socket.connected && currentUser) {
      debugLog("Socket is connected, setting up listeners");
      setupSocketListeners();
    } else {
      debugLog("Socket not ready for listeners:", {
        hasSocket: !!socket,
        connected: socket?.connected,
        hasCurrentUser: !!currentUser
      });
    }

    // Cleanup function
    return () => {
      if (socket) {
        debugLog("Cleaning up socket listeners");
        socket.off("newMessage");
      }
    };
  }, [socket, socket?.connected, currentUser, selectedUser, isDraftConversation]);

  // Load users when authenticated
  useEffect(() => {
    if (isAuthenticated && currentUser) {
      debugLog("Loading users on authentication");
      getUsers();
    }
  }, [isAuthenticated, currentUser]);

  const value = {
    messages,
    users,
    selectedUser,
    unseenMessages,
    loading,
    error,
    listingContext,
    isDraftConversation, // NEW: Expose draft state
    getUsers,
    getMessages,
    sendMessage,
    setSelectedUser: setSelectedUserNormal, // Use normal selection
    setSelectedUserWithListing, // Use draft-aware selection
    setMessages,
    setUnseenMessages,
    markMessagesAsSeen,
    reportConversation,
    socket,
    isUserOnline,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};